import { useUpdateUserTimezoneMutation } from '@cheqroom/api';
import { DateTime, resolveBrowserTimezoneSync } from '@cheqroom/date-time';
import { useCallback, useEffect, useRef, useState } from 'react';

export type UseSyncBrowserTimezoneToProfileOptions = {
	/** Authenticated user id. Also gates the run — see the note on load ordering above. */
	userId?: string | null;
	/** Stored profile zone. Empty means "no zone stored", so pass it only once loaded. */
	profileTimezone?: string | null;
	/** When false, skip entirely (e.g. impersonation / unauthenticated). Default true when userId set. */
	enabled?: boolean;
	onSynced?: (timezone: string) => void;
};

export type PendingTimezoneAck = {
	browserTimezone: string;
	profileTimezone: string;
};

/**
 * Syncs the browser IANA timezone onto the user profile (legacy parity).
 * Silent update when only the IANA name differs (or the profile has no zone at all);
 * notice + confirm when the offsets differ.
 *
 * `userId` doubles as the "profile is loaded" signal: both apps read `id` and `timezone` off the
 * same user object, so an empty `profileTimezone` alongside a `userId` means the user genuinely
 * has no stored zone rather than that the profile is still in flight.
 */
export const useSyncBrowserTimezoneToProfile = ({
	userId,
	profileTimezone,
	enabled = true,
	onSynced,
}: UseSyncBrowserTimezoneToProfileOptions): {
	pendingAck: PendingTimezoneAck | null;
	confirmPendingAck: () => Promise<void>;
} => {
	const { mutateAsync: updateTimezone } = useUpdateUserTimezoneMutation();
	const [pendingAck, setPendingAck] = useState<PendingTimezoneAck | null>(null);
	const ranForUserRef = useRef<string | null>(null);
	const onSyncedRef = useRef(onSynced);
	onSyncedRef.current = onSynced;

	const persist = useCallback(
		async (timezone: string) => {
			if (!userId) return;
			await updateTimezone({ userId, timezone });
			onSyncedRef.current?.(timezone);
		},
		[updateTimezone, userId]
	);

	useEffect(() => {
		if (!enabled || !userId) {
			setPendingAck(null);
			return;
		}

		// Run once per authenticated user id (avoid loops after onSynced updates profileTimezone).
		if (ranForUserRef.current === userId) return;

		const decision = resolveBrowserTimezoneSync(profileTimezone, DateTime.getTimeZone());
		if (decision.action === 'none') {
			ranForUserRef.current = userId;
			return;
		}

		if (decision.action === 'silent') {
			ranForUserRef.current = userId;
			void persist(decision.browserTimezone).catch(() => {
				ranForUserRef.current = null;
			});
			return;
		}

		ranForUserRef.current = userId;
		setPendingAck({
			browserTimezone: decision.browserTimezone,
			profileTimezone: decision.profileTimezone,
		});
	}, [enabled, userId, profileTimezone, persist]);

	const confirmPendingAck = useCallback(async () => {
		if (!pendingAck) return;
		const { browserTimezone } = pendingAck;
		setPendingAck(null);
		try {
			await persist(browserTimezone);
		} catch {
			ranForUserRef.current = null;
			setPendingAck(pendingAck);
		}
	}, [pendingAck, persist]);

	return { pendingAck, confirmPendingAck };
};
